import { useEffect, useMemo, useState } from "react";
import { Box, makeStyles } from "@material-ui/core";
import Slider, { Settings } from "react-slick";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { debounce } from "lodash";

import { QuoteLineItem } from "../../../../api/Quote/QuoteLineItem";
import { hasAddons, hasSubscriptions, quateAddonQuantityValue } from "../../../../app/utils";
import { getFirstImage, ProductSubscription } from "../../../../api/Product/ProductSubscriptionApi";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { SliderItem } from "./SliderItem";
import { Quote } from "../../../../api/Quote/Quote";
import { EEventType, eventAnalytic } from "../../../../app/analytics";
import {
  addAddonQuote,
  addSubscriptionQuote,
  removeAddonQuote,
  removeSubscriptionQuote
} from "../../../../store/QuoteSlice";
import { AddonForm } from "../../../../api/Addon/AddonApi";
import {
  QuoteSubscriptionForm,
  mapProductSubscriptionToForm
} from "../../../../api/QuoteSubscription/QuoteSubscriptionApi";
import { history } from "../../../../app/rootReducer";
import AddonModal from "./AddonModal";
import { EQuoteStatus } from "../../../../api/Quote/EQuoteStatus";

interface Props {
  quote: Quote;
  addons?: QuoteLineItem[];
  subscriptions: ProductSubscription[] | null;
}

const QuoteAddons = ({ addons, subscriptions, quote }: Props) => {
  const { isMoreThanMd, isMoreThanSm } = useBreakpoint();
  const dispatch = useDispatch();
  const [addonSelected, setAddonSelected] = useState<QuoteLineItem>();
  const [open, setOpen] = useState(false);

  const totalLength = (subscriptions?.length ?? 0) + (addons?.length ?? 0);

  const length = totalLength > 2 ? 3 : totalLength;
  useEffect(() => {
    if (!addons) return;
    setAddonSelected((prev) => {
      return addons.find((item) => item.productId === prev?.productId);
    });
  }, [addons]);

  const slideCount = useMemo(() => {
    if (length >= 3) return isMoreThanMd ? length : isMoreThanSm ? 2 : 1;
    return isMoreThanSm ? length : 1;
  }, [isMoreThanMd, isMoreThanSm, length]);

  const classes = useStyles(`${180 / Math.ceil(totalLength / slideCount)}px`)();

  if (!hasAddons(addons) && !hasSubscriptions(subscriptions)) {
    return null;
  }

  const settings: Settings = {
    dots: !isMoreThanMd,
    infinite: true,
    speed: 500,
    arrows: false,
    slidesToShow: slideCount,
    slidesToScroll: slideCount,
    className: classes.slider
  };
  const onAddAddon = (addon: QuoteLineItem) => {
    if (addon.selected) {
      onRemoveAddon(addon);
      return;
    }
    eventAnalytic.record(addon);
    dispatch(
      addAddonQuote(quote.id, {
        addonId: addon.productId,
        quantity: addon.quantity
      } as AddonForm)
    );
    toast.success(addon.name + " has been added");
  };

  const onRemoveAddon = (addon: QuoteLineItem) => {
    dispatch(
      removeAddonQuote(quote.id, {
        addonId: addon.productId
      } as AddonForm)
    );
    eventAnalytic.record(EEventType.RemovedAddon);
    toast.success(addon.name + " has been removed");
  };

  const onAddSubscription = (data: ProductSubscription) => {
    const selected = quote.subscriptions?.some((i) => i.productId === data.id);
    if (selected) {
      onRemoveSubscription(data);
      return;
    }
    eventAnalytic.record(data);
    dispatch(addSubscriptionQuote(quote.id, mapProductSubscriptionToForm(data)));
    toast.success(data.name + " has been added");
  };

  const onRemoveSubscription = (data: ProductSubscription) => {
    dispatch(
      removeSubscriptionQuote(quote.id, {
        productId: data.id
      } as QuoteSubscriptionForm)
    );
    eventAnalytic.record(EEventType.RemovedSubscription);
    toast.success(data.name + " has been removed");
  };

  const isSentQuote = quote.status === EQuoteStatus.Sent;

  return (
    <>
      <Box
        p={isMoreThanMd ? 3 : 0}
        pb={isMoreThanMd ? 0 : 3}
        mt={isMoreThanMd ? 0 : 1}
        maxWidth={"1068px"}
        width={"100%"}
        textAlign="center"
      >
        <Slider {...settings} autoplay={true} autoplaySpeed={8000}>
          {hasAddons(addons) &&
            addons!.map((addon, idx) => (
              <SliderItem
                imgUrl={addon.imageUrl}
                name={addon.name}
                price={quateAddonQuantityValue(addon)}
                selected={addon.selected}
                onAdd={() => onAddAddon(addon)}
                onView={() => {
                  setOpen(true);
                  setAddonSelected(addon);
                }}
                viewOnly={!isSentQuote}
                key={`addon-${idx}`}
              />
            ))}
          {hasSubscriptions(subscriptions) &&
            subscriptions!.map((subscription, idx) => (
              <SliderItem
                imgUrl={getFirstImage(subscription)?.url}
                name={subscription.name}
                price={subscription.prices[0].price}
                paymentType={subscription.prices[0].paymentType}
                selected={quote.subscriptions?.some((i) => i.productId === subscription.id)}
                onAdd={() => onAddSubscription(subscription)}
                onView={() => history.push("/protect")}
                viewOnly={!isSentQuote}
                key={`subscription-${idx}`}
              />
            ))}
        </Slider>
      </Box>
      <AddonModal
        quoteId={quote.id}
        open={open}
        data={addonSelected}
        viewOnly={!isSentQuote}
        handleClose={() => {
          setOpen(false);
          debounce(() => setAddonSelected(undefined), 500)();
        }}
        onAddAddon={onAddAddon}
      />
    </>
  );
};

export default QuoteAddons;
const useStyles = (dotWidth: string) =>
  makeStyles((theme) => ({
    slider: {
      "& .slick-dots": {
        zIndex: "1 !important"
      },
      "& .slick-dots li": {
        width: dotWidth,
        height: "2px",
        background: "#706483"
      },
      "& .slick-dots li.slick-active": {
        background: "#f7815c"
      },

      "& .slick-dots li button:before": {
        content: "none"
      }
    }
  }));
